// StressChartBase.tsx
import React, { useMemo, useRef } from "react";
import Chart, {
  Series,
  ArgumentAxis,
  ValueAxis,
  Tooltip,
  Legend,
  ZoomAndPan,
  ScrollBar,
  CommonAxisSettings,
  CommonSeriesSettings,
  Label,
} from "devextreme-react/chart";
import "./StressChartBase.css";

function stddev(arr: number[]): number {
  const xs = arr.filter((v) => Number.isFinite(v));
  const n = xs.length;
  if (n <= 1) return 0;
  const mean = xs.reduce((a, b) => a + b, 0) / n;
  const varPop = xs.reduce((s, x) => s + (x - mean) ** 2, 0) / n;
  return Math.sqrt(varPop);
}

// High-contrast palette for TOP N (stable order: rank 1..N)
const CONTRAST_5: string[] = ["#00E5FF", "#FF1744", "#00E676", "#FFEA00", "#B388FF"];

interface StressChartScaffoldProps {
  data: Record<string, any>[];
  elements: string[];
  showLegend: boolean;
  onLegendClick: (e: any) => void;
  legendItemsMapper: (items: any[]) => any[];
  seriesRenderer: (name: string) => React.ReactNode;
  valueAxisExtras: React.ReactNode;
  height?: string | number;
  colorMap: Record<string, string>;
}

export const StressChartScaffold: React.FC<StressChartScaffoldProps> = ({
  data,
  elements,
  showLegend,
  onLegendClick,
  legendItemsMapper,
  seriesRenderer,
  valueAxisExtras,
  height,
  colorMap,
}) => {
  const chartRef = useRef<any>(null);

  const xMax = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return 24;
    let m = 24;
    for (const r of data) {
      const v = Number(r?.rel_month);
      if (Number.isFinite(v) && v > m) m = v;
    }
    return m;
  }, [data]);

  return (
    <Chart
      ref={chartRef}
      dataSource={data}
      onLegendClick={onLegendClick}
      size={{ width: "100%" as any, height: (height ?? "100%") as any }}
      margin={{ top: 6, right: 8, bottom: 4, left: 8 }}
      redrawOnResize
      // @ts-ignore
      customizeSeries={(seriesInfo: any) => {
        const name = seriesInfo?.name;
        const c = name ? colorMap?.[name] : null;
        return c ? { color: c, width: 3, opacity: 0.95 } : { opacity: 0.25, width: 1 };
      }}
    >
      <Tooltip
        enabled
        shared
        zIndex={2147483000}
        customizeTooltip={(info: any) => {
          const header = `Month ${info.argumentText}`;

          const pts = (info?.points || [])
            .filter((p: any) => p && p.seriesName != null)
            .sort((a: any, b: any) => {
              const av = Number(a.value);
              const bv = Number(b.value);
              if (!Number.isFinite(av) && !Number.isFinite(bv)) return 0;
              if (!Number.isFinite(av)) return 1;
              if (!Number.isFinite(bv)) return -1;
              return Math.abs(bv) - Math.abs(av);
            });

          const lines = pts
            .map((p: any) => {
              const v = Number(p.value);
              const txt = Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : "—";
              return `<div style="display:flex;justify-content:space-between;gap:12px;white-space:nowrap;">
                        <span>${p.seriesName}</span>
                        <b>${txt}</b>
                      </div>`;
            })
            .join("");

          return {
            html: `
              <div class="stress-tooltip-inner">
                <div class="stress-tooltip-title">${header}</div>
                ${lines}
              </div>
            `,
          };
        }}
      />

      <CommonAxisSettings
        grid={{ visible: true }}
        tick={{ visible: true }}
        label={{ overlappingBehavior: "stagger" }}
      />

      <CommonSeriesSettings argumentField="rel_month" hoverMode="allArgumentPoints" />

      <ArgumentAxis
        argumentType="numeric"
        wholeRange={{ startValue: 0, endValue: xMax }}
        tickInterval={1}
        minorTickInterval={1}
        title="Relative month"
      />

      <ValueAxis>
        {valueAxisExtras}
        <Label
          customizeText={(e: any) => {
            const v = Number(e.value);
            if (!Number.isFinite(v)) return "";
            return `${(v * 100).toFixed(0)}%`;
          }}
        />
      </ValueAxis>

      {Array.isArray(elements) ? elements.map(seriesRenderer) : null}

      <ZoomAndPan argumentAxis="both" />
      <ScrollBar visible />

      <Legend
        customizeItems={legendItemsMapper}
        visible={showLegend}
        position="outside"
        orientation="horizontal"
        verticalAlignment="bottom"
        horizontalAlignment="center"
        columnCount={10}
        markerSize={10}
        itemTextPosition="right"
        paddingLeftRight={4}
        paddingTopBottom={2}
        font={{ size: 13 }}
      />
    </Chart>
  );
};

interface ViewPnl {
  data: Record<string, any>[];
  elements: string[];
}

interface StressChartLinesProps {
  viewPnl: ViewPnl;
  height?: string | number;
  topN?: number;
}

export const StressChartLines: React.FC<StressChartLinesProps> = ({ viewPnl, height = "100%", topN = 5 }) => {
  const data = viewPnl?.data ?? [];
  const elements = viewPnl?.elements ?? [];

  const topVolRank = useMemo(() => {
    if (!data.length || !elements.length) return [];
    const scores = elements.map((name) => {
      const vals = data.map((r) => {
        const n = Number(r?.[name]);
        return Number.isFinite(n) ? n : NaN;
      });
      return { name, vol: stddev(vals) };
    });
    scores.sort((a, b) => b.vol - a.vol);
    return scores.slice(0, topN).map((x) => x.name);
  }, [data, elements, topN]);

  const topVolSet = useMemo(() => new Set(topVolRank), [topVolRank]);

  const colorMap = useMemo(() => {
    const m: Record<string, string> = {};
    topVolRank.forEach((name, i) => {
      m[name] = CONTRAST_5[i % CONTRAST_5.length];
    });
    return m;
  }, [topVolRank]);

  const onLegendClick = (e: any) => {
    const series = e.target;
    if (!series) return;
    series.isVisible() ? series.hide() : series.show();
  };

  const seriesRenderer = (name: string) => (
    <Series
      key={name}
      type="line"
      ignoreEmptyPoints
      argumentField="rel_month"
      valueField={name}
      name={name}
      color={colorMap?.[name]}
      visible={topVolSet.has(name)}
      point={{ visible: false }}
      width={2}
    />
  );

  return (
    <StressChartScaffold
      data={data}
      elements={elements}
      showLegend={true}
      onLegendClick={onLegendClick}
      legendItemsMapper={(items) => items}
      seriesRenderer={seriesRenderer}
      height={height}
      valueAxisExtras={null}
      colorMap={colorMap}
    />
  );
};
